import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import { getChannelStates, LOG_CHANNELS, type LogChannel, resetAllChannels, setChannelLevel } from "@/logger";

interface LogChannelsModalProps {
  open: boolean;
  onClose: () => void;
}

const LEVELS = ["TRACE", "DEBUG", "INFO", "WARN", "ERROR", "SILENT"] as const;

const levelColor: Record<string, string> = {
  TRACE: "#90caf9",
  DEBUG: "#4caf50",
  INFO: "#2196f3",
  WARN: "#ff9800",
  ERROR: "#d32f2f",
  SILENT: "#666",
};

export default function LogChannelsModal({ open, onClose }: LogChannelsModalProps) {
  const [channels, setChannels] = useState<{ channel: LogChannel; level: string }[]>([]);

  const refresh = useCallback(() => setChannels(getChannelStates()), []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleLevelChange = (channel: LogChannel, event: SelectChangeEvent) => {
    setChannelLevel(channel, event.target.value as (typeof LEVELS)[number]);
    refresh();
  };

  const handleVerboseAll = () => {
    for (const ch of LOG_CHANNELS) setChannelLevel(ch, "DEBUG");
    refresh();
  };

  const handleSilentAll = () => {
    for (const ch of LOG_CHANNELS) setChannelLevel(ch, "SILENT");
    refresh();
  };

  const handleReset = () => {
    resetAllChannels();
    refresh();
  };

  const activeCount = channels.filter((c) => c.level === "DEBUG" || c.level === "TRACE").length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>Log Channels</span>
        {activeCount > 0 && <Chip label={`${activeCount} active`} size="small" color="success" variant="outlined" />}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 1, marginBottom: 2 }}>
          <Button variant="outlined" size="small" onClick={handleVerboseAll}>
            Verbose All
          </Button>
          <Button variant="outlined" size="small" onClick={handleSilentAll}>
            Silent All
          </Button>
          <Button variant="outlined" size="small" color="secondary" onClick={handleReset}>
            Reset
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 450 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Channel</TableCell>
                <TableCell align="right">Level</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.map(({ channel, level }) => (
                <TableRow key={channel} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "13px",
                        fontWeight: level === "DEBUG" || level === "TRACE" ? 600 : 400,
                        color: levelColor[level] ?? "#fff",
                      }}
                    >
                      {channel}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Select
                      size="small"
                      value={level}
                      onChange={(e) => handleLevelChange(channel, e)}
                      sx={{ fontFamily: "monospace", fontSize: "12px", minWidth: 100 }}
                    >
                      {LEVELS.map((l) => (
                        <MenuItem key={l} value={l} sx={{ fontFamily: "monospace", fontSize: "12px" }}>
                          {l}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 1 }}>
          Changes take effect immediately and persist across page reloads.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
